import { CloseOutlined } from '@ant-design/icons';
import { useLeadsCRUDActions } from '@mm/state/actions/LeadsCRUDActions';
import { LeadsSearchQuery, SearchAttributes } from '@mm/state/interfaces/leads';
import _, { isEqual } from 'lodash';
import React, { useState } from 'react';
import { RemoteSearchInput } from '../../../../../../../common/components/RemoteSearchInput/RemoteSearchInput';
import { convertoUpperSnakeCase } from '../helpers';
import styles from './LocationInputFilter.module.css';

export const LOCATION_FIELDS = [
  'MUNICIPALITY',
  'ADDRESS',
  'CITY',
  'STATE',
  'ZIP',
  'STATE',
  'COUNTY',
];

export const LocationInputFilter = ({
  disabled,
  filterParams,
  setFilterParams,
  exclude,
}: {
  disabled?: boolean;
  filterParams: LeadsSearchQuery;
  setFilterParams: (params: LeadsSearchQuery) => void;
  exclude?: boolean;
}) => {
  const { retrieveLocationAttributes } = useLeadsCRUDActions();

  const [locationSearchValue, setLocationSearchValue] = useState('');
  const [locationResults, setLocationResults] = useState<SearchAttributes>({});
  const params = exclude ? filterParams.exclude : filterParams.query;

  const selectedLocations = params?.filter(arr =>
    arr.some(i => LOCATION_FIELDS.includes(i)),
  );

  const mappedLocationResults = Object.entries(locationResults).flatMap(
    item => {
      return item[1].map(i => {
        return {
          key: item[0],
          name: i,
        };
      });
    },
  );

  const fetchLocationAttributes = async query => {
    setLocationSearchValue(query);
    if (query) {
      const res: SearchAttributes = await retrieveLocationAttributes(query);
      if (res) {
        setLocationResults(res);
      }
    }
  };

  const renderSuggestion = (suggestion: { key: string; name: string }) => {
    return (
      <div className={styles.suggestionContainer} key={suggestion.key}>
        <div className={styles.suggestion}>{suggestion.name}</div>
        {suggestion.key && <div className={styles.key}>{suggestion.key}</div>}
      </div>
    );
  };

  const onSelectedItem = (item: { key: string; name: string }) => {
    const key = convertoUpperSnakeCase(item.key);

    const itemExists = params?.find(
      arr => arr[0] === key && arr[1] === item.name,
    );
    let updatedQuery;

    if (!itemExists) {
      if (exclude) {
        updatedQuery = {
          query: filterParams.query,
          exclude: [...(filterParams.exclude || []), [key, item.name]],
          offset: 0,
        } as LeadsSearchQuery;
      } else {
        updatedQuery = {
          query: [...(filterParams.query || []), [key, item.name]],
          exclude: filterParams.exclude,
          offset: 0,
        } as LeadsSearchQuery;
      }
      setFilterParams(updatedQuery);
    }
  };

  const isLastIncludedLocationItem =
    filterParams.query.filter(i => LOCATION_FIELDS.find(key => i.includes(key)))
      .length === 1;

  const onDeleteItem = (item: [string, string]) => {
    let updatedQuery;

    if (exclude) {
      updatedQuery = {
        query: filterParams.query,
        exclude: [
          ...(filterParams?.exclude?.filter(arr => !isEqual(arr, item)) || []),
        ],
        offset: filterParams.offset,
      };
    } else {
      updatedQuery = {
        query: [...filterParams.query.filter(arr => !isEqual(arr, item))],
        exclude: isLastIncludedLocationItem ? [] : filterParams.exclude,
        offset: filterParams.offset,
      };
    }

    setFilterParams(updatedQuery);
  };

  return (
    <div className={styles.container}>
      <RemoteSearchInput
        value={locationSearchValue}
        placeholder="County, City, State, Zip Code"
        lengthLimit={1}
        disabled={disabled}
        onFetch={fetchLocationAttributes}
        results={mappedLocationResults}
        onSelectedItem={onSelectedItem}
        renderSuggestion={renderSuggestion}
        className={styles.input}
        style={{ border: '1px solid var(--color-grey-5)', borderRadius: '4px' }}
        withSearchIcon
      />
      <div className={styles.locations}>
        {selectedLocations?.map(locationArr => {
          return (
            <div
              className={styles.locationItemWrapper}
              key={`${locationArr[0]}_${locationArr[1]}`}
            >
              <span className={styles.key}>
                {locationArr[0].toLocaleLowerCase()}:
              </span>
              <span> {locationArr[1]}</span>
              <CloseOutlined
                className={styles.close}
                disabled={!filterParams.query.length}
                onClick={() => onDeleteItem(locationArr)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
